import { Injectable } from '@angular/core';
import { CurrentRouteData } from '@app/routing/models/current-route-data';
import { RouterService } from '@app/routing/router.service';
import { loggerFor } from '@app/utils/logger';
import { isNotBlank } from '@app/utils/string';
import { StringMap } from '@app/utils/string-map';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';

/**
 * Portal Browser Document Service
 */
@Injectable({
  providedIn: 'root',
})
export class PortalWindowDocumentService {
  private baseTitle: string = 'Portal';
  private breadcrumb: string = '';
  private logger = loggerFor('WindowDocumentService');
  private title: string = '';

  private language$ = new Subject<void>();

  constructor(
    private routerService: RouterService,
    private translateService: TranslateService
  ) {
    this.translateService.onLangChange.subscribe(() => {
      this.language$.next();
    });

    combineLatest([this.language$, this.routerService.currentRouteData$]).subscribe(([, routeData]) => {
      if (routeData != null) {
        const { breadcrumb, route } = this.routeToString(routeData);

        this.updateDocumentTitle(route, breadcrumb);
      }
    });
  }

  public setBaseTitle(baseTitle: string): void {
    this.baseTitle = baseTitle;

    this.updateDocumentTitle();
  }

  public translateRouteNameWith(params: StringMap<any>) {
    const route = this.routerService.currentRouteData.name;

    this.useTitle(`route.${route}`, params);
  }

  /**
   * Updates the document title to: "${baseTitle} ${title}"
   * @param title
   * @param breadcrumb
   */
  public updateDocumentTitle(title: string = this.title, breadcrumb: string = this.breadcrumb) {
    // Remember the breadcrumb and title
    this.breadcrumb = breadcrumb;
    this.title = title;

    this.updateTitle();
  }

  /**
   * Translates the given key and then updates the title
   *
   * @param i18nKey Translation key
   * @param params Optional parameters that are passed to the translateService
   */
  private useTitle(i18nKey: string, params?: StringMap<any>) {
    this.updateDocumentTitle(this.translateService.instant(i18nKey, params));
  }

  /**
   * Create a string version of the current menu
   * @param currentRoute
   */
  private routeToString(currentRoute: CurrentRouteData) {
    let parts: string[] = [];

    if (currentRoute.params.serviceProviderId != null) {
      parts.push(currentRoute.params.serviceProviderId);
    }

    if (currentRoute.params.enterpriseId != null) {
      parts.push(currentRoute.params.enterpriseId);
    }

    if (currentRoute.params.userId != null) {
      parts.push(currentRoute.params.userId);
    }

    const translatedRouteName = this.translateService.instant(
      `route.${currentRoute.name}`,
      currentRoute.params
    );

    return {
      breadcrumb: parts.join(' / '),
      route: translatedRouteName,
    };
  }

  /**
   * Calculate the new title and update the document.title property
   */
  private updateTitle() {
    let newTitle = this.baseTitle;

    const breadcrumbNotBlank = isNotBlank(this.breadcrumb);
    const titleNotBlank = isNotBlank(this.title);

    if (breadcrumbNotBlank || titleNotBlank) {
      newTitle += ' - ';
    }

    if (breadcrumbNotBlank) {
      newTitle += this.breadcrumb;

      if (titleNotBlank) {
        newTitle += ' / ';
      }
    }

    if (titleNotBlank) {
      newTitle += this.title;
    }

    // Set the document title
    document.title = newTitle;

    this.logger.debug(`Updated document title to:`, document.title);
  }
}
